import { useTranslation } from 'react-i18next';
import type { FlightTicket, TicketCondition, TicketSegment } from '@/lib/flight-ticket/api';

// The e-ticket on paper. Vendors issue no ticket file, so this sheet IS the document the
// operator prints or saves as PDF — the same data the passenger receives by email. Airport
// times are local wall-clock values: formatted as they come, never converted. A test ticket
// carries a printed watermark, because the paper travels without the console around it.
// No amount is printed: the order's total / base / tax and a change or refund penalty are what
// Nexa paid the vendor, and the airline sees only prices (commercial-contract §6.13).

function wallClock(local: string): Date | undefined {
  const date = new Date(`${local.slice(0, 19)}Z`);
  return Number.isFinite(date.getTime()) ? date : undefined;
}
function day(local: string, language: string): string {
  const date = wallClock(local);
  return date
    ? new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
    : local.slice(0, 10);
}
const clock = (local: string) => local.slice(11, 16);
/** ISO-8601 duration (`PT2H21M`) → hours and minutes; undefined when unreadable. */
function duration(iso: string | undefined): { hours: number; minutes: number } | undefined {
  const match = iso ? /^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?$/.exec(iso) : null;
  if (!match) return undefined;
  const hours = Number(match[1] ?? 0) * 24 + Number(match[2] ?? 0);
  const minutes = Number(match[3] ?? 0);
  return hours || minutes ? { hours, minutes } : undefined;
}

function SegmentRow({ segment, language }: { segment: TicketSegment; language: string }) {
  const { t } = useTranslation('flightTicket');
  const span = duration(segment.duration);
  const spanText = span
    ? [span.hours ? t('durationHours', { count: span.hours }) : '', span.minutes ? t('durationMinutes', { count: span.minutes }) : '']
        .filter(Boolean)
        .join(' ')
    : undefined;
  const meta = [spanText, segment.aircraft, segment.passengers[0]?.cabin].filter(Boolean).join(' · ');
  const end = (iata: string, name: string | undefined, terminal: string | undefined, local: string, align: 'left' | 'right') => (
    <div className={align === 'right' ? 'text-right' : ''}>
      <p className="text-[20pt] font-bold leading-none tabular-nums">{clock(local)}</p>
      <p className="mt-1 text-[12pt] font-bold">{iata}</p>
      {name ? <p className="text-[8.5pt] text-neutral-600">{name}</p> : null}
      {terminal ? <p className="text-[8.5pt]">{t('terminal', { terminal })}</p> : null}
    </div>
  );
  return (
    <div className="break-inside-avoid rounded border border-neutral-300 p-[4mm]" data-testid="ticket-segment">
      <p className="text-[9pt]">
        <span className="font-bold">{segment.flightNumber}</span> · {day(segment.departureLocal, language)}
        {segment.operatingAirline !== segment.marketingAirline ? (
          <span className="text-neutral-600"> · {t('operatedBy', { carrier: segment.operatingFlightNumber ?? segment.operatingAirline })}</span>
        ) : null}
      </p>
      <div className="mt-[3mm] grid grid-cols-[1fr_auto_1fr] items-center gap-[4mm]">
        {end(segment.origin, segment.originName ?? segment.originCity, segment.originTerminal, segment.departureLocal, 'left')}
        <p className="text-center text-[8pt] text-neutral-500">
          →{meta ? <><br />{meta}</> : null}
        </p>
        {end(segment.destination, segment.destinationName ?? segment.destinationCity, segment.destinationTerminal, segment.arrivalLocal, 'right')}
      </div>
    </div>
  );
}

function ConditionLine({ label, condition, testId }: { label: string; condition: TicketCondition | undefined; testId: string }) {
  const { t } = useTranslation('flightTicket');
  if (!condition) return null;
  // A penalty PRESENT — priced, or `null` without a price card — means a fee applies; only an
  // absent one means none. Truthiness would read an unpriced fee as «Permitido».
  const withFee = condition.penalty !== undefined;
  return (
    <div className="flex justify-between gap-4">
      <dt>{label}</dt>
      <dd className="font-medium" data-testid={testId}>
        {!condition.allowed ? t('notAllowed') : withFee ? t('allowedWithFee') : t('allowed')}
      </dd>
    </div>
  );
}

export function FlightTicketSheet({ ticket, brand }: { ticket: FlightTicket; brand: string }) {
  const { t, i18n } = useTranslation('flightTicket');
  const language = i18n.language;
  const segments = ticket.slices.flatMap((slice) => slice.segments);
  const first = segments[0];
  const fareBrand = ticket.slices.map((slice) => slice.fareBrand).find(Boolean);
  const reference = ticket.bookingReference ?? ticket.orderId;
  return (
    <article
      data-print-sheet
      data-testid="flight-ticket-sheet"
      className="relative mx-auto w-full max-w-[210mm] space-y-[5mm] overflow-hidden bg-white p-[10mm] text-[9.5pt] leading-snug text-black shadow-sm print:shadow-none"
    >
      {ticket.mode === 'sandbox' ? (
        <span
          aria-hidden
          data-testid="flight-ticket-watermark"
          className="pointer-events-none absolute inset-0 flex select-none items-center justify-center text-center text-[34pt] font-bold uppercase leading-tight tracking-[0.15em] text-black opacity-[0.07] print:opacity-[0.1]"
          style={{ transform: 'rotate(-24deg)' }}
        >
          {t('sandboxWatermark')}
        </span>
      ) : null}
      <header className="flex items-start justify-between gap-6 border-b-2 border-black pb-[3mm]">
        <div>
          <p className="text-[8pt] font-bold uppercase tracking-[0.18em] text-neutral-600">{brand}</p>
          <h1 className="text-[18pt] font-bold">{t('documentTitle')}</h1>
          <p className="text-[10pt] font-semibold">{ticket.airline.name}{ticket.airline.iata ? ` (${ticket.airline.iata})` : ''}</p>
        </div>
        <div className="text-right">
          <p className="text-[8pt] uppercase tracking-[0.12em] text-neutral-600">{t('bookingReference')}</p>
          <p className="font-mono text-[22pt] font-bold tracking-[0.12em]" data-testid="flight-ticket-reference">{reference}</p>
          {ticket.issuedAt ? (
            <p className="text-[8pt] text-neutral-600">
              {t('issuedAt')} {new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ticket.issuedAt))}
            </p>
          ) : null}
        </div>
      </header>

      {ticket.mode === 'sandbox' ? (
        <p className="border-2 border-dashed border-black px-3 py-1 text-center text-[10pt] font-bold uppercase tracking-[0.12em]" data-testid="flight-ticket-sandbox">
          {t('sandboxWatermark')}
        </p>
      ) : null}
      {ticket.status === 'FAILED' ? (
        <p className="border-2 border-black px-3 py-1 text-center text-[10pt] font-bold" role="alert">{t('cancelled')}</p>
      ) : null}

      <section className="space-y-[3mm]">
        {segments.map((segment) => (
          <SegmentRow key={`${segment.flightNumber}-${segment.departureLocal}`} segment={segment} language={language} />
        ))}
        <p className="text-[8pt] text-neutral-600">{t('localTimes')}</p>
      </section>

      <section>
        <h2 className="mb-[1.5mm] text-[8pt] font-bold uppercase tracking-[0.14em] text-neutral-600">{t('passengers')}</h2>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-black text-[8pt] uppercase tracking-[0.1em] text-neutral-600">
              <th className="py-[1.5mm] font-semibold">{t('passenger')}</th>
              <th className="py-[1.5mm] font-semibold">{t('ticketNumber')}</th>
              <th className="py-[1.5mm] font-semibold">{t('baggage')}</th>
            </tr>
          </thead>
          <tbody>
            {ticket.passengers.map((passenger) => {
              const detail = first?.passengers.find((p) => p.passengerId === passenger.id);
              const bags = (detail?.baggage ?? []).filter((b) => b.quantity > 0)
                .map((b) => t(b.type === 'checked' ? 'checked' : 'carryOn', { count: b.quantity }));
              return (
                <tr key={passenger.id} className="border-b border-neutral-200 align-top" data-testid="ticket-passenger">
                  <td className="py-[2mm] font-semibold">{`${passenger.givenName} ${passenger.familyName}`}</td>
                  <td className="py-[2mm] font-mono">{passenger.ticketNumbers.join(', ') || '—'}</td>
                  <td className="py-[2mm]">
                    {bags.length ? bags.join(' · ') : t('noBaggage')}
                    {detail?.seat ? ` · ${t('seat', { seat: detail.seat })}` : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="sm:max-w-[50%]">
        <dl className="space-y-[1mm]">
          <dt className="text-[8pt] font-bold uppercase tracking-[0.14em] text-neutral-600">
            {t('fare')}{fareBrand ? ` · ${fareBrand}` : ''}
          </dt>
          <ConditionLine label={t('refund')} condition={ticket.conditions.refundBeforeDeparture} testId="ticket-refund" />
          <ConditionLine label={t('change')} condition={ticket.conditions.changeBeforeDeparture} testId="ticket-change" />
        </dl>
      </section>

      <footer className="space-y-[1mm] border-t border-neutral-300 pt-[3mm] text-[8.5pt]">
        <p>{t('checkIn', { reference, airline: ticket.airline.name })}</p>
        {ticket.airline.conditionsOfCarriageUrl ? (
          <p className="break-all text-neutral-600">{t('carriage', { airline: ticket.airline.name })}: {ticket.airline.conditionsOfCarriageUrl}</p>
        ) : null}
        <p className="font-mono text-[7.5pt] text-neutral-500">{t('orderReference')}: {ticket.orderId}</p>
      </footer>
    </article>
  );
}
